import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faRunning,
  faChalkboardTeacher,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "@lib/firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { SectionQuicklinks } from "@cakrawalajakarta/shared-components";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@cakrawalajakarta/shared-components/common/NotificationContext";
import axios from "axios";

// Lazy load GPXMap
const GPXMap = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.GPXMap,
  }))
);

// Skeleton Loader
const SkeletonLoader = () => (
  <div className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center items-center bg-gray-100">
    <div className="relative z-0 container mx-auto px-4">
      <div className="bg-white rounded-2xl border border-slate-200 text-left relative overflow-hidden max-w-4xl mx-auto">
        <div className="w-full h-80 bg-gray-200 animate-pulse"></div>
        <div className="p-4 space-y-4">
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

// Utility untuk debounce
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Utility untuk format tanggal ke "D MMMM YYYY"
const formatDate = (dateString) => {
  if (!dateString) return "Tanggal belum ditentukan";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Tanggal belum ditentukan";
  } catch {
    return "Tanggal belum ditentukan";
  }
};

// Utility untuk caching ke localStorage
const cacheEventData = (slug, distance, data) => {
  try {
    localStorage.setItem(
      `category_${slug}_${distance}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {}
};

const getCachedEventData = (slug, distance) => {
  try {
    const cached = localStorage.getItem(`category_${slug}_${distance}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000; // Cache selama 5 menit
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`category_${slug}_${distance}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

export const KategoriDetail = React.memo(() => {
  const { slug, distance } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("map");
  const [elevationImageUrl, setElevationImageUrl] = useState(null);
  const [eventImageUrl, setEventImageUrl] = useState(null);
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);

  // Fungsi untuk menentukan ikon berdasarkan tipe event
  const getEventIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "trail_running":
        return { icon: faRunning, size: "text-5xl" };
      case "seminar":
        return { icon: faChalkboardTeacher, size: "text-4xl" };
      default:
        return { icon: faCalendarAlt, size: "text-4xl" };
    }
  };

  const fetchCollectionName = useCallback(async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventRef);
      return eventDoc.exists() ? eventDoc.data().collectionName : null;
    } catch {
      return null;
    }
  }, []);

  const checkRegistration = useCallback(
    async (collectionName, eventId) => {
      if (!user || !eventId || !collectionName) {
        return { isRegistered: false, status: null, token: null };
      }
      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;
          if (
            (status === "settlement" &&
              (paymentType === "free" || harga === 0)) ||
            (status === "pending" && paymentType !== "free") ||
            (status === "settlement" &&
              paymentType !== "free" &&
              data.snapToken)
          ) {
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);
            if (status === "pending" && data.snapToken && data.orderId) {
              const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                  data.orderId
                }`,
                { params: { eventId } }
              );
              const updatedStatus = response.data.transaction_status;
              if (updatedStatus !== status) {
                if (
                  updatedStatus === "pending" ||
                  updatedStatus === "settlement"
                ) {
                  await updateDoc(doc.ref, {
                    transaction_status: updatedStatus,
                  });
                  return {
                    isRegistered: true,
                    status: updatedStatus,
                    token: data.snapToken,
                  };
                } else {
                  await deleteDoc(doc.ref);
                  return { isRegistered: false, status: null, token: null };
                }
              }
            }
            return { isRegistered: true, status, token: data.snapToken };
          }
          await deleteDoc(doc.ref);
        }
        setRegistrationStatus("not_registered");
        setTransactionStatus(null);
        setSnapToken(null);
        return { isRegistered: false, status: null, token: null };
      } catch {
        setRegistrationStatus("not_registered");
        setTransactionStatus(null);
        setSnapToken(null);
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user]
  );

  const fetchData = useCallback(async () => {
    try {
      const cachedData = getCachedEventData(slug, distance);
      if (cachedData) {
        setEvent(cachedData.event);
        setCategory(cachedData.category);
        setEventImageUrl(cachedData.eventImageUrl);
        setElevationImageUrl(cachedData.elevationImageUrl);
        setCollectionName(cachedData.collectionName);
        setLoading(false);
        if (cachedData.collectionName && user && cachedData.event.eventId) {
          await checkRegistration(
            cachedData.collectionName,
            cachedData.event.eventId
          );
        }
        return cachedData;
      }
      const eventRef = doc(db, "semuaKegiatan", slug);
      const eventDoc = await getDoc(eventRef);
      if (!eventDoc.exists()) {
        setError("Kegiatan tidak ditemukan");
        setLoading(false);
        return null;
      }
      const eventData = { id: eventDoc.id, ...eventDoc.data() };
      const eventImage =
        eventData.image || "https://via.placeholder.com/800x400";
      setEventImageUrl(eventImage);
      const categories = eventData.categories || [];
      if (categories.length === 0) {
        setError("Tidak ada kategori yang tersedia");
        setLoading(false);
        return null;
      }
      const distanceInt = parseInt(distance);
      const selectedCategory =
        eventData.type === "seminar"
          ? categories[0]
          : categories.find((cat) => cat.distance === distanceInt);
      if (!selectedCategory) {
        setError(
          eventData.type === "seminar"
            ? "Kategori seminar tidak ditemukan"
            : `Kategori ${distance} KM tidak ditemukan`
        );
        setLoading(false);
        return null;
      }
      setCategory(selectedCategory);
      const elevationImage =
        selectedCategory.imageUrl || "https://via.placeholder.com/800x400";
      setElevationImageUrl(elevationImage);
      setEvent(eventData);
      const dataToCache = {
        event: eventData,
        category: selectedCategory,
        eventImageUrl: eventImage,
        elevationImageUrl: elevationImage,
        collectionName: null,
      };
      let fetchedCollectionName = null;
      if (eventData.eventId) {
        fetchedCollectionName = await fetchCollectionName(eventData.eventId);
        setCollectionName(fetchedCollectionName);
        dataToCache.collectionName = fetchedCollectionName;
      }
      cacheEventData(slug, distance, dataToCache);
      setLoading(false);
      return dataToCache;
    } catch (err) {
      setError("Gagal memuat data: " + err.message);
      setLoading(false);
      return null;
    }
  }, [slug, distance, user, fetchCollectionName]);

  useEffect(() => {
    let mounted = true;
    const fetchAllData = async () => {
      const eventData = await fetchData();
      if (!eventData || !mounted) return;
      if (eventData.event.eventId && user) {
        try {
          const fetchedCollectionName = await fetchCollectionName(
            eventData.event.eventId
          );
          if (mounted && fetchedCollectionName) {
            setCollectionName(fetchedCollectionName);
            await checkRegistration(
              fetchedCollectionName,
              eventData.event.eventId
            );
            const updatedCache = {
              ...eventData,
              collectionName: fetchedCollectionName,
            };
            cacheEventData(slug, distance, updatedCache);
          }
        } catch {}
      }
    };
    fetchAllData();
    return () => {
      mounted = false;
    };
  }, [fetchData, user, fetchCollectionName, slug, distance]);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (!isSnapReady || !window.snap) {
      showNotification("error", "Midtrans belum siap. Silakan coba lagi.");
      return;
    }
    if (!snapToken) {
      showNotification("error", "Token pembayaran tidak ditemukan.");
      return;
    }
    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Pembayaran berhasil!");
          await checkRegistration(collectionName, event.eventId);
          navigate("/pesanansaya");
        },
        onPending: async () => {
          showNotification("warning", "Pembayaran sedang diproses.");
          await checkRegistration(collectionName, event.eventId);
          navigate("/pesanansaya");
        },
        onError: () => {
          showNotification("error", "Pembayaran gagal. Silakan coba lagi.");
        },
        onClose: () => {},
      });
    } catch {
      showNotification("error", "Gagal memproses pembayaran.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(() => {
    if (
      !event ||
      !event.title ||
      !category ||
      category.price === undefined ||
      !event.eventId
    ) {
      showNotification("error", "Data event atau kategori tidak lengkap.");
      return;
    }
    if (!user) {
      showNotification(
        "error",
        "Silakan login terlebih dahulu untuk mendaftar."
      );
      navigate("/login");
      return;
    }
    if (!collectionName) {
      showNotification("error", "Gagal memuat data event. Silakan coba lagi.");
      return;
    }
    if (registrationStatus === "registered") {
      if (transactionStatus === "settlement") {
        showNotification("info", "Anda sudah terdaftar pada event ini.");
      } else if (transactionStatus === "pending") {
        handleBayar();
      }
      return;
    }
    const title =
      event.type === "seminar"
        ? event.title
        : `${event.title} - ${category.distance} KM`;
    const path =
      event.type === "seminar"
        ? `/formseminar/${event.slug}`
        : `/form/${event.slug}`;
    navigate(path, {
      state: {
        title,
        price: category.price ?? event.price ?? 0,
        eventId: event.eventId,
      },
    });
  }, [
    event,
    category,
    user,
    collectionName,
    registrationStatus,
    transactionStatus,
    showNotification,
    navigate,
    handleBayar,
  ]);

  const handleExportGpx = useCallback(
    debounce(async (gpxFileUrl) => {
      try {
        const response = await fetch(gpxFileUrl, { mode: "cors" });
        const gpxText = await response.text();
        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                category.distance
              }km.gpx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch {
        showNotification("error", "Gagal mengunduh file GPX.");
      }
    }, 500),
    [event, category, showNotification]
  );

  const handleExportElevationImage = useCallback(
    debounce(async () => {
      try {
        const response = await fetch(elevationImageUrl, { mode: "cors" });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}-elevation.png`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                category.distance
              }km-elevation.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch {
        showNotification("error", "Gagal mengunduh gambar elevasi.");
      }
    }, 500),
    [event, category, elevationImageUrl, showNotification]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isRegistrationOpen = useMemo(
    () => event?.status === "Registrasi Dibuka",
    [event]
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error || !event || !category) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center items-center bg-gray-100">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-cyan-600"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          KATEGORI
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-cyan-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          {error || "TIDAK DITEMUKAN"}
        </motion.p>
        <Link to="/kegiatan" className="text-cyan-600 underline">
          Kembali ke Daftar Kegiatan
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center items-center bg-gray-100">
      <div className="relative z-0 container mx-auto px-4">
        <motion.div
          className="bg-white rounded-2xl border border-slate-200 text-left overflow-hidden max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <div className="relative w-full h-80">
            <img
              src={eventImageUrl}
              alt={event.title}
              className="w-full h-80 object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-slate-600 bg-opacity-50 flex flex-col items-center justify-center gap-6">
              <FontAwesomeIcon
                icon={getEventIcon(event.type).icon}
                className={`text-white ${getEventIcon(event.type).size}`}
                aria-label={`Ikon ${
                  event.type === "trail_running" ? "Trail Running" : "Seminar"
                }`}
              />
              <div className="text-center">
                <h3 className="text-2xl md:text-4xl font-black uppercase text-white px-4">
                  {event.title}
                </h3>
                {event.type === "trail_running" && category && (
                  <p className="text-lg md:text-xl uppercase text-white mt-2">
                    Kategori {category.distance} KM
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="hidden md:block">
              <table className="w-full table-auto divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Tanggal
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Jam
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Lokasi
                    </th>
                    {event.type === "trail_running" && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Jarak
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Elevation Gain
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          COT
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {formatDate(category.date)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {category.time || "-"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {event.location || "-"}
                    </td>
                    {event.type === "trail_running" && (
                      <>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {category.distance !== undefined
                            ? `${category.distance} KM`
                            : "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {category.elevationGain !== undefined
                            ? `${category.elevationGain} M`
                            : "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {category.cot !== undefined
                            ? `${category.cot} Jam`
                            : "-"}
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="md:hidden">
              <div className="space-y-4 py-4 bg-white">
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Tanggal
                  </div>
                  <div className="text-sm text-gray-700">
                    {formatDate(category.date)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Jam
                  </div>
                  <div className="text-sm text-gray-700">
                    {category.time || "-"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Lokasi
                  </div>
                  <div className="text-sm text-gray-700">
                    {event.location || "-"}
                  </div>
                </div>
                {event.type === "trail_running" && (
                  <>
                    <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                      <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                        Jarak
                      </div>
                      <div className="text-sm text-gray-700">
                        {category.distance !== undefined
                          ? `${category.distance} KM`
                          : "-"}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                      <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                        Elevation Gain
                      </div>
                      <div className="text-sm text-gray-700">
                        {category.elevationGain !== undefined
                          ? `${category.elevationGain} M`
                          : "-"}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                      <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                        COT
                      </div>
                      <div className="text-sm text-gray-700">
                        {category.cot !== undefined
                          ? `${category.cot} Jam`
                          : "-"}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="py-4 text-cyan-600">
            <p className="px-4 mb-4 text-lg leading-relaxed text-gray-700">
              {category.description || "Tidak ada deskripsi tersedia."}
            </p>
            {event.type === "trail_running" && category.gpxFileUrl && (
              <div>
                <h4 className="px-4 text-md font-normal text-cyan-600 mb-4">
                  {viewMode === "map" && "MAP & GPX"}
                  {viewMode === "elevation" && "PROFIL ELEVASI"}
                  {viewMode === "rundown" && "RUNDOWN"}
                </h4>
                {viewMode === "map" && (
                  <div className="px-4 relative w-full h-[400px] rounded-lg overflow-hidden">
                    <Suspense
                      fallback={
                        <div className="w-full h-full bg-gray-200 animate-pulse"></div>
                      }
                    >
                      <GPXMap gpxFileUrl={category.gpxFileUrl} />
                    </Suspense>
                  </div>
                )}
                {viewMode === "elevation" && elevationImageUrl && (
                  <div className="px-4">
                    <div className="relative w-full h-[400px] rounded-lg max-w-full overflow-x-auto">
                      <div className="h-full min-w-max flex items-center">
                        <img
                          src={elevationImageUrl}
                          alt="Elevation Profile"
                          className="h-full w-auto"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {viewMode === "rundown" && (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Kegiatan
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Tanggal
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Jam
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Lokasi
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.rundown && category.rundown.length > 0 ? (
                          category.rundown.map((entry, index) => (
                            <tr
                              key={index}
                              className="border-b border-gray-200 hover:bg-gray-50"
                            >
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {entry.activity || "-"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {formatDate(entry.date)}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {entry.time || "-"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700">
                                {entry.location || "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-4 py-4 text-sm text-gray-700 text-center"
                            >
                              Tidak ada data rundown tersedia.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {event.type === "trail_running" && category.gpxFileUrl && (
            <>
              <div className="md:hidden bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex flex-col divide-y divide-gray-300 border-y border-gray-300">
                  <div className="relative">
                    <button
                      className={`w-full uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                        viewMode === "map"
                          ? "bg-cyan-600 font-normal text-white"
                          : "hover:bg-gray-200"
                      }`}
                      onClick={() => setViewMode("map")}
                    >
                      MAP & GPX
                    </button>
                    {viewMode === "map" && (
                      <button
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-lg"
                        onClick={() => handleExportGpx(category.gpxFileUrl)}
                        title="Unduh GPX"
                      >
                        <FontAwesomeIcon icon={faFileDownload} />
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <button
                      className={`w-full uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                        viewMode === "elevation"
                          ? "bg-cyan-600 font-normal text-white"
                          : "hover:bg-gray-200"
                      }`}
                      onClick={() => setViewMode("elevation")}
                    >
                      PROFIL ELEVASI
                    </button>
                    {viewMode === "elevation" && (
                      <button
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-lg"
                        onClick={() => handleExportElevationImage()}
                        title="Unduh Gambar Elevasi"
                      >
                        <FontAwesomeIcon icon={faFileDownload} />
                      </button>
                    )}
                  </div>
                  <button
                    className={`w-full uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                      viewMode === "rundown"
                        ? "bg-cyan-600 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setViewMode("rundown")}
                  >
                    RUNDOWN
                  </button>
                </div>
              </div>
              <div className="hidden md:flex items-center justify-between bg-gray-200 text-base font-normal text-gray-400 border-b border-gray-300">
                <div>
                  {viewMode === "map" && (
                    <button
                      className="text-cyan-600 px-4 flex items-center space-x-2"
                      onClick={() => handleExportGpx(category.gpxFileUrl)}
                    >
                      <span>Unduh GPX</span>
                      <FontAwesomeIcon icon={faFileDownload} />
                    </button>
                  )}
                  {viewMode === "elevation" && (
                    <button
                      className="text-cyan-600 px-4 flex items-center space-x-2"
                      onClick={() => handleExportElevationImage()}
                    >
                      <span>Unduh PNG</span>
                      <FontAwesomeIcon icon={faFileDownload} />
                    </button>
                  )}
                </div>
                <div className="flex space-x-0 divide-x divide-gray-300">
                  <button
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                      viewMode === "map"
                        ? "bg-gray-100 font-normal text-cyan-600"
                        : "hover:bg-gray-300"
                    }`}
                    onClick={() => setViewMode("map")}
                  >
                    MAP & GPX
                  </button>
                  <button
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                      viewMode === "elevation"
                        ? "bg-gray-100 font-normal text-cyan-600"
                        : "hover:bg-gray-300"
                    }`}
                    onClick={() => setViewMode("elevation")}
                  >
                    PROFIL ELEVASI
                  </button>
                  <button
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                      viewMode === "rundown"
                        ? "bg-gray-100 font-normal text-cyan-600"
                        : "hover:bg-gray-300"
                    }`}
                    onClick={() => setViewMode("rundown")}
                  >
                    RUNDOWN
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="mb-4">
            <div className="hidden md:block">
              <table className="w-full table-auto divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Harga
                    </th>
                    {event.eventId && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Peserta
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Navigasi
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-700 flex items-center">
                      <div
                        className={`w-2.5 h-2.5 rounded-full mr-2 ${
                          event.status === "Registrasi Dibuka"
                            ? "bg-green-500"
                            : event.status === "Registrasi Ditutup"
                            ? "bg-red-500"
                            : "bg-yellow-500"
                        }`}
                      ></div>
                      {event.status}
                    </td>
                    <td className="px-4 py-4 text-sm font-semibold text-cyan-700">
                      {category.price !== undefined
                        ? category.price > 0
                          ? `Rp ${category.price.toLocaleString("id-ID")}`
                          : "Gratis"
                        : event.price > 0
                        ? `Rp ${event.price.toLocaleString("id-ID")}`
                        : "Gratis"}
                    </td>
                    {event.eventId && (
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <Link
                          to={`/entrylist/${event.eventId}`}
                          className="text-cyan-600 underline"
                        >
                          Lihat Peserta
                        </Link>
                      </td>
                    )}
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <Link
                        to="/"
                        className="text-cyan-600 underline hover:text-cyan-800"
                      >
                        Beranda
                      </Link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="md:hidden">
              <div className="space-y-4 py-4 bg-white">
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </div>
                  <div className="text-sm text-gray-700 flex items-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mr-2 ${
                        event.status === "Registrasi Dibuka"
                          ? "bg-green-500"
                          : event.status === "Registrasi Ditutup"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></div>
                    {event.status}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Harga
                  </div>
                  <div className="text-sm font-semibold text-cyan-600">
                    {category.price !== undefined
                      ? category.price > 0
                        ? `Rp ${category.price.toLocaleString("id-ID")}`
                        : "Gratis"
                      : event.price > 0
                      ? `Rp ${event.price.toLocaleString("id-ID")}`
                      : "Gratis"}
                  </div>
                </div>
                {event.eventId && (
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Peserta
                    </div>
                    <div className="text-sm text-gray-700">
                      <Link
                        to={`/entrylist/${event.eventId}`}
                        className="text-cyan-600 underline"
                      >
                        Lihat Peserta
                      </Link>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Navigasi
                  </div>
                  <div className="text-sm text-gray-700">
                    <Link
                      to="/"
                      className="text-cyan-600 underline hover:text-cyan-800"
                    >
                      Beranda
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-cyan-900">
            <p className="px-4 mb-4 text-lg leading-relaxed text-gray-700">
              {event.aboutEvent || "Tidak ada deskripsi kegiatan tersedia."}
            </p>
          </div>

          {event.type === "trail_running" && <SectionQuicklinks />}

          <div className="mt-4">
            <div className="hidden md:block">
              <div className="flex items-center justify-end bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex justify-end space-x-0 divide-x divide-gray-300">
                  {registrationStatus === "registered" &&
                  transactionStatus === "settlement" ? (
                    <span className="flex-1 text-center uppercase px-4 py-4 bg-cyan-600 text-white whitespace-nowrap">
                      ANDA SUDAH TERDAFTAR
                    </span>
                  ) : registrationStatus === "registered" &&
                    transactionStatus === "pending" ? (
                    <button
                      onClick={handleBayar}
                      className="flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 bg-green-600 font-normal text-white whitespace-nowrap hover:bg-green-800"
                      disabled={!isSnapReady || !snapToken}
                    >
                      SELESAIKAN PEMBAYARAN
                    </button>
                  ) : (
                    /*
                      <button
                        onClick={handleRegister}
                        className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-green-600 font-normal text-white hover:bg-green-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        DAFTAR KEGIATAN
                      </button>
                      */ null
                      
                  )}
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <div className="bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex flex-col divide-y divide-gray-300">
                  <div>
                    {registrationStatus === "registered" &&
                    transactionStatus === "settlement" ? (
                      <span className="w-full text-center uppercase px-4 py-4 bg-cyan-600 text-white block transition-colors duration-300 whitespace-nowrap">
                        ANDA SUDAH TERDAFTAR
                      </span>
                    ) : registrationStatus === "registered" &&
                      transactionStatus === "pending" ? (
                      <button
                        onClick={handleBayar}
                        className="w-full text-center uppercase px-4 py-4 block transition-colors duration-300 bg-green-600 font-normal text-white whitespace-nowrap hover:bg-green-800"
                        disabled={!isSnapReady || !snapToken}
                      >
                        SELESAIKAN PEMBAYARAN
                      </button>
                    ) : ( 
                      /*
                      <button
                        onClick={handleRegister}
                        className={`w-full text-center uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-green-600 font-normal text-white hover:bg-green-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        DAFTAR KEGIATAN
                      </button>
                      */ null
                      
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
});
